import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import apiService from '../services/api';
import RewardPreviewModal from '../components/RewardPreviewModal';
import '../styles/StaffPages.css';

// 'Baikal Book' only ships a normal weight (styles/fonts.css), so asking it for
// fontWeight 600/700 makes the browser synthesise a fake bold. Emphasis has to come
// from the Medium/Bold families instead.
const FONT_BOOK = '"Baikal Book", sans-serif';
const FONT_MEDIUM = '"Baikal Medium", sans-serif';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'cancelled', label: 'Cancelled' },
];

const KIND_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'redemption', label: 'Redeemed' },
  { value: 'raffle_win', label: 'Raffle win' },
  { value: 'weekly_prize', label: 'Weekly prize' },
  { value: 'overall_prize', label: 'Season prize' },
];

const KIND_LABELS = {
  redemption: 'Redeemed',
  raffle_win: 'Raffle win',
  weekly_prize: 'Weekly prize',
  overall_prize: 'Season prize',
};

// Tints the status selector so the queue can still be scanned at a glance now that
// the standalone pill is gone.
const STATUS_COLORS = {
  pending: { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' },
  contacted: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  fulfilled: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5' },
};

const cell = {
  padding: '18px 16px',
  // Table cells default to content-box, which would add this padding on top of each
  // column's declared width and blow the table ~270px past its intended size.
  boxSizing: 'border-box',
  fontFamily: FONT_BOOK,
  fontSize: '14px',
  lineHeight: 1.45,
  color: '#111827',
  borderBottom: '1px solid #f3f4f6',
  textAlign: 'left',
  verticalAlign: 'middle',
};

const headCell = {
  ...cell,
  padding: '14px 16px',
  fontFamily: FONT_MEDIUM,
  fontSize: '11px',
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

// Keeps the first and last columns off the card's rounded edge.
const edgeLeft = { paddingLeft: '22px' };
const edgeRight = { paddingRight: '22px' };

function formatPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length !== 10) return digits || '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function NotebookIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 3.5h11a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 3.5v17M4.5 6.5h2.5M4.5 12h2.5M4.5 17.5h2.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M10.5 9h6M10.5 13h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Shown under whichever of Email / Phone the student actually asked to be reached
// on. The only place the product captures this today is the first-reward claim
// flow, so most rows will carry nothing.
function PreferredTag() {
  return (
    <div
      style={{
        marginTop: '5px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontFamily: FONT_MEDIUM,
        fontSize: '10px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: '#0b6f8f',
      }}
    >
      <span aria-hidden="true">★</span> Preferred
    </div>
  );
}

// Facts that only make sense for a won/redeemed item, not for a reward definition.
function buildFulfillmentFacts(row) {
  const detail = row.reward_detail || {};
  const points = detail.points_cost ?? row.points;
  const facts = [['Type', KIND_LABELS[row.kind] || row.kind]];
  if (points != null && points !== '') {
    facts.push(['Points', `${Number(points).toLocaleString()} pts`]);
  }
  facts.push(['Won by', row.user_name], ['Date', formatDate(row.created_at)]);
  if (detail.merchant_name) facts.push(['Merchant', detail.merchant_name]);
  return facts;
}

function NotesPanel({ row, width, onSaved, onError }) {
  const [draft, setDraft] = useState(row.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(row.notes || '');
    setSaved(false);
  }, [row.notes]);

  const dirty = draft !== (row.notes || '');

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiService.updateStaffFulfillmentNotes(row.source, row.id, draft);
      onSaved(draft);
      setSaved(true);
    } catch (err) {
      onError(err.response?.data?.error || err.message || 'Failed to save notes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // sticky + left:0 keeps the panel against the viewport's left edge as the table
    // scrolls sideways; the measured width stops it running past the right edge.
    <div
      style={{
        position: 'sticky',
        left: 0,
        width: width ? `${width}px` : '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        padding: '16px 22px 22px',
        background: '#fafafa',
      }}
    >
      <label
        style={{
          display: 'block',
          fontSize: '11px',
          color: '#6b7280',
          marginBottom: '8px',
          fontFamily: FONT_MEDIUM,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Notes
      </label>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        maxLength={5000}
        placeholder="Anything worth remembering — how they were contacted, pickup arrangements, sizing…"
        style={{
          width: '100%',
          padding: '12px 14px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          fontFamily: FONT_BOOK,
          fontSize: '14px',
          lineHeight: 1.5,
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="staff-page-button"
          style={{ padding: '6px 16px', opacity: saving || !dirty ? 0.5 : 1 }}
        >
          <span className="staff-page-button-text">{saving ? 'Saving…' : 'Save notes'}</span>
        </button>
        {dirty && !saving && (
          <span style={{ fontSize: '12px', color: '#92400e' }}>Unsaved changes</span>
        )}
        {saved && !dirty && <span style={{ fontSize: '12px', color: '#065f46' }}>Saved</span>}
      </div>
    </div>
  );
}

const StaffFulfillment = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [modalRow, setModalRow] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  // The notes row spans the full (scrollable) table width, so on a narrow viewport
  // part of it sits off-screen. Measuring the card's visible width lets the panel
  // pin itself to exactly what the user can see.
  const scrollRef = useRef(null);
  const [visibleWidth, setVisibleWidth] = useState(0);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;
    const measure = () => setVisibleWidth(node.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getStaffFulfillment();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to load rewards to give out.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (kindFilter !== 'all' && row.kind !== kindFilter) return false;
      if (!q) return true;
      return [row.user_name, row.user_email, row.user_phone, row.reward_title]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [rows, statusFilter, kindFilter, search]);

  const patchRow = (row, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.source === row.source && r.id === row.id ? { ...r, ...patch } : r))
    );
  };

  const handleStatusChange = async (row, nextStatus) => {
    const key = `${row.source}:${row.id}`;
    setSavingKey(key);
    setError('');
    try {
      await apiService.updateStaffFulfillmentStatus(row.source, row.id, nextStatus);
      patchRow(row, { status: nextStatus });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update status.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="fulfillment" />
      <div className="admin-main">
        <div className="admin-content">
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              marginBottom: '16px',
            }}
          >
            <div>
              <h1 className="staff-page-header">Reward Delivery</h1>
              <p className="staff-page-subheader">
                Students who redeemed or won a reward, and how to reach them.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, reward…"
                style={{
                  padding: '9px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontFamily: FONT_BOOK,
                  fontSize: '14px',
                  minWidth: '240px',
                }}
              />
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                style={{
                  padding: '9px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontFamily: FONT_BOOK,
                  fontSize: '14px',
                }}
              >
                {KIND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '9px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontFamily: FONT_BOOK,
                  fontSize: '14px',
                }}
              >
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px 14px',
                borderRadius: '8px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                fontFamily: FONT_BOOK,
                fontSize: '13px',
              }}
            >
              {error}
            </div>
          )}

          <div
            ref={scrollRef}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
              overflowX: 'auto',
            }}
          >
            {loading ? (
              <p style={{ ...cell, borderBottom: 'none', color: '#6b7280' }}>
                Loading rewards…
              </p>
            ) : visible.length === 0 ? (
              <p style={{ ...cell, borderBottom: 'none', color: '#6b7280' }}>
                {rows.length === 0
                  ? 'No rewards to give out yet.'
                  : 'No rewards match this filter.'}
              </p>
            ) : (
              // tableLayout 'fixed' is what makes the per-column widths below
              // authoritative. Under the default 'auto', they are only hints and the
              // browser still sizes by content — which handed the email column all
              // the slack and squeezed Reward.
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                  minWidth: '1114px',
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...headCell, ...edgeLeft, width: 150 }}>Name</th>
                    <th style={{ ...headCell, width: 190 }}>Email</th>
                    <th style={{ ...headCell, width: 125 }}>Phone</th>
                    <th style={{ ...headCell, width: 105 }}>Type</th>
                    {/* Widest column: reward titles are the longest free text here and
                        were being squeezed by email's auto-sizing. */}
                    <th style={{ ...headCell, width: 232 }}>Reward</th>
                    <th style={{ ...headCell, width: 105 }}>Date</th>
                    <th style={{ ...headCell, width: 135 }}>Status</th>
                    <th style={{ ...headCell, ...edgeRight, width: 72 }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => {
                    const rowKey = `${row.source}:${row.id}`;
                    const tone = STATUS_COLORS[row.status] || STATUS_COLORS.pending;
                    const isExpanded = expandedKey === rowKey;
                    const hasNotes = Boolean((row.notes || '').trim());
                    return (
                      // Key belongs on the fragment, not the inner <tr>: the
                      // fragment is what the map returns.
                      <Fragment key={rowKey}>
                        <tr>
                          <td style={{ ...cell, ...edgeLeft, fontFamily: FONT_MEDIUM }}>{row.user_name}</td>
                          <td style={{ ...cell, color: '#4b5563', overflowWrap: 'break-word' }}>
                            <div>{row.user_email}</div>
                            {row.contact_preference === 'email' && <PreferredTag />}
                          </td>
                          <td style={{ ...cell, color: '#4b5563', whiteSpace: 'nowrap' }}>
                            {/* Fall back to the number they gave when stating the
                                preference, if their profile has none. */}
                            <div>
                              {row.user_phone
                                ? formatPhone(row.user_phone)
                                : row.contact_preference === 'sms' && row.contact_preference_value
                                  ? formatPhone(row.contact_preference_value)
                                  : '—'}
                            </div>
                            {row.contact_preference === 'sms' && <PreferredTag />}
                          </td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                            {KIND_LABELS[row.kind] || row.kind}
                          </td>
                          <td style={cell}>
                            <button
                              type="button"
                              onClick={() => setModalRow(row)}
                              style={{
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                color: '#1976d2',
                                fontFamily: FONT_MEDIUM,
                                fontSize: '14px',
                                textAlign: 'left',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                cursor: 'pointer',
                              }}
                            >
                              {row.reward_title}
                            </button>
                          </td>
                          <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                            {formatDate(row.created_at)}
                          </td>
                          <td style={cell}>
                            <select
                              value={row.status}
                              disabled={savingKey === rowKey}
                              onChange={(e) => handleStatusChange(row, e.target.value)}
                              style={{
                                padding: '7px 12px',
                                border: `1px solid ${tone.border}`,
                                borderRadius: '100px',
                                backgroundColor: tone.bg,
                                color: tone.fg,
                                fontFamily: FONT_MEDIUM,
                                fontSize: '13px',
                                cursor: savingKey === rowKey ? 'wait' : 'pointer',
                              }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option
                                  key={opt.value}
                                  value={opt.value}
                                  style={{ backgroundColor: 'white', color: '#111827' }}
                                >
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ ...cell, ...edgeRight }}>
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              title={
                                isExpanded
                                  ? 'Hide notes'
                                  : hasNotes
                                    ? 'View notes'
                                    : 'Add notes'
                              }
                              aria-label={
                                isExpanded
                                  ? 'Hide notes'
                                  : hasNotes
                                    ? 'View notes'
                                    : 'Add notes'
                              }
                              onClick={() => setExpandedKey(isExpanded ? null : rowKey)}
                              style={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: `1px solid ${isExpanded || hasNotes ? '#0091BF' : '#d1d5db'}`,
                                borderRadius: '50%',
                                background: isExpanded || hasNotes ? '#e6f6fb' : '#fff',
                                color: isExpanded || hasNotes ? '#0b6f8f' : '#6b7280',
                                cursor: 'pointer',
                              }}
                            >
                              <NotebookIcon />
                              {/* A written-on row is worth spotting without opening it. */}
                              {hasNotes && !isExpanded && (
                                <span
                                  aria-hidden="true"
                                  style={{
                                    position: 'absolute',
                                    top: -2,
                                    right: -2,
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    background: '#0091BF',
                                    border: '2px solid #fff',
                                  }}
                                />
                              )}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} style={{ padding: 0, borderBottom: '1px solid #f3f4f6' }}>
                              <NotesPanel
                                row={row}
                                width={visibleWidth}
                                onSaved={(notes) => patchRow(row, { notes })}
                                onError={setError}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modalRow && (
        <RewardPreviewModal
          title={modalRow.reward_title}
          detail={modalRow.reward_detail || {}}
          facts={buildFulfillmentFacts(modalRow)}
          onClose={() => setModalRow(null)}
        />
      )}
    </div>
  );
};

export default StaffFulfillment;
